import React, { useEffect, useState } from 'react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle,
  Droplet,
  Flame,
  Leaf,
  DollarSign,
  Zap,
  Shield,
  Wrench,
  Sun,
  Snowflake,
  Volume2,
  Clock,
  Award,
  TrendingDown,
  MapPin,
  FileCheck,
  Phone,
  ThermometerSun,
  HeartHandshake,
  Sparkles,
  Home as HomeIcon,
  Info,
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { useEstimateModal } from '../components/EstimateModalContext';

// Brand data — real manufacturer logos + iconified feature pills
const brands = [
  {
    name: 'Reclaim Energy',
    slug: 'reclaim',
    logo: '/brand-logos/reclaim.png',
    tagline: 'Premium CO₂ heat pump · Australian engineered',
    pills: [
      { icon: Leaf, label: 'CO₂ Refrigerant' },
      { icon: MapPin, label: 'Aussie Engineered' },
      { icon: Volume2, label: 'Ultra-Quiet' },
    ],
  },
  {
    name: 'Sanden',
    slug: 'sanden',
    logo: '/brand-logos/sanden.svg',
    tagline: 'Japanese CO₂ heat pump pioneer',
    pills: [
      { icon: Award, label: 'CO₂ Pioneer' },
      { icon: Shield, label: '15yr Lifespan' },
      { icon: Snowflake, label: 'Cold-Climate' },
    ],
  },
  {
    name: 'iStore',
    slug: 'istore',
    logo: '/brand-logos/istore.png',
    logoBg: 'bg-[#1f3a5f]',
    tagline: 'PV-smart · integrated tank design',
    pills: [
      { icon: Sun, label: 'Solar-Smart' },
      { icon: Droplet, label: '270L Tank' },
      { icon: Zap, label: 'R290 Eco-Gas' },
    ],
  },
  {
    name: 'Aquatech',
    slug: 'aquatech',
    logo: '/brand-logos/aquatech.png',
    tagline: 'Family-owned · all-in-one design',
    pills: [
      { icon: HeartHandshake, label: 'Family-Owned' },
      { icon: HomeIcon, label: 'All-in-One' },
      { icon: Wrench, label: 'Easy Install' },
    ],
  },
  {
    name: 'Stiebel Eltron',
    slug: 'stiebel',
    logo: '/brand-logos/stiebel.svg',
    tagline: 'German engineered · premium efficiency',
    pills: [
      { icon: Award, label: 'German Engineered' },
      { icon: TrendingDown, label: 'High Efficiency' },
      { icon: Shield, label: 'Premium Build' },
    ],
  },
  {
    name: 'Neopower',
    slug: 'neopower',
    logo: '/brand-logos/neopower.png',
    tagline: 'High-COP performance · rebate friendly',
    pills: [
      { icon: Zap, label: 'High COP' },
      { icon: FileCheck, label: 'Rebate-Ready' },
      { icon: DollarSign, label: 'Great Value' },
    ],
  },
  {
    name: 'Econova',
    slug: 'econova',
    logo: '/brand-logos/econova.png',
    tagline: 'Compact split-system design',
    pills: [
      { icon: HomeIcon, label: 'Compact Split' },
      { icon: Leaf, label: 'Eco-Friendly' },
      { icon: Volume2, label: 'Quiet Op.' },
    ],
  },
  {
    name: 'Haier',
    slug: 'haier',
    logo: '/brand-logos/haier.svg',
    tagline: 'Global leader · integrated smart controls',
    pills: [
      { icon: Award, label: 'Global Top-5 Appliances*' },
      { icon: Sparkles, label: 'Smart Controls' },
      { icon: Shield, label: 'Trusted Brand' },
    ],
  },
  {
    name: 'Emerald',
    slug: 'emerald',
    logo: '/brand-logos/emerald.png',
    tagline: 'Australian brand · robust all-in-one',
    pills: [
      { icon: MapPin, label: 'Aussie Brand' },
      { icon: HomeIcon, label: 'All-in-One' },
      { icon: Shield, label: 'Robust Build' },
    ],
  },
  {
    name: 'Midea',
    slug: 'midea',
    logo: '/brand-logos/midea.png',
    tagline: 'World-leading HVAC manufacturer · value performance',
    pills: [
      { icon: Award, label: 'Major Global HVAC*' },
      { icon: DollarSign, label: 'Best Value' },
      { icon: Zap, label: 'Reliable' },
    ],
  },
];

const benefits = [
  {
    icon: TrendingDown,
    title: 'Slash Bills by Up To 75%*',
    desc: 'Heat pumps use 1 unit of electricity to produce 3–5 units of heat.* Save up to $1,000+ per year vs electric storage and gas systems.*',
  },
  {
    icon: Leaf,
    title: 'Cut Carbon Emissions',
    desc: 'Up to 70% less CO₂* vs traditional gas or electric storage. Pair with solar to go effectively zero-emissions.',
  },
  {
    icon: Sun,
    title: 'Pairs Perfectly with Solar',
    desc: 'Run your heat pump during daylight to use your own free solar energy — turn excess solar into free hot water.*',
  },
  {
    icon: Shield,
    title: 'Long Lifespan & Warranty',
    desc: '10–15 year lifespan* with 5–10 year tank warranties.* Premium models like Reclaim & Sanden last even longer.',
  },
  {
    icon: Volume2,
    title: 'Whisper-Quiet Operation',
    desc: 'Modern units run as low as 37–48 dB* — quieter than a fridge. Great for tight blocks and shared walls.',
  },
  {
    icon: Snowflake,
    title: 'Australian-Climate Ready',
    desc: 'Performs efficiently from -10°C to 43°C ambient* — suitable for every state, including alpine zones.',
  },
];

const howItWorks = [
  {
    step: '01',
    title: 'Absorbs Heat from Air',
    desc: 'A fan draws in ambient air and passes it across an evaporator with refrigerant.',
  },
  {
    step: '02',
    title: 'Compresses & Heats',
    desc: 'A compressor pressurises the refrigerant, dramatically raising its temperature.',
  },
  {
    step: '03',
    title: 'Transfers Heat to Water',
    desc: 'Heat is exchanged into the water tank — heating water to 60°C+ for sanitation.',
  },
  {
    step: '04',
    title: 'Hot Water on Tap',
    desc: 'Stored hot water is ready 24/7. Smart controls run on cheaper off-peak or solar.',
  },
];

const rebates = [
  {
    state: 'Federal — All States',
    icon: '🇦🇺',
    title: 'STCs (Small-scale Technology Certificates)',
    program: 'SRES — Small-scale Renewable Energy Scheme',
    value: '$700 – $1,800*',
    detail:
      'Generated upfront when you install an eligible heat pump. Number of STCs depends on your zone, model and capacity. Discount applied at point of sale.',
    pill: 'Available Nationwide',
    pillColor: 'bg-[#B5D43B] text-[#3D5A47]',
  },
  {
    state: 'Victoria',
    icon: '🟦',
    title: 'VEECs + Solar Vic Hot Water Rebate',
    program: 'Victorian Energy Upgrades (VEU) + Solar Victoria',
    value: '$1,000 – $2,800*',
    detail:
      'VEECs generated under VEU plus up to $1,000 Solar Vic hot water rebate (means-tested). Stackable with federal STCs.',
    pill: 'VIC',
    pillColor: 'bg-blue-100 text-blue-800',
  },
  {
    state: 'New South Wales',
    icon: '🟥',
    title: 'PDRS / ESCs',
    program: 'Peak Demand Reduction Scheme + Energy Savings Scheme',
    value: '$800 – $2,200*',
    detail:
      'Significant point-of-sale discount through accredited providers. Stackable with federal STCs for maximum savings.',
    pill: 'NSW',
    pillColor: 'bg-red-100 text-red-800',
  },
  {
    state: 'South Australia',
    icon: '🟨',
    title: 'REPS',
    program: 'Retailer Energy Productivity Scheme',
    value: '$400 – $1,200*',
    detail:
      'Discount applied via REPS-accredited installers. Targeted incentives for low-income and concession holders.',
    pill: 'SA',
    pillColor: 'bg-yellow-100 text-yellow-800',
  },
  {
    state: 'Queensland',
    icon: '🟧',
    title: 'Federal STC + QLD Concessions',
    program: 'SRES',
    value: '$700 – $1,800*',
    detail:
      'Full federal STC discount plus QLD electricity rebate concessions for eligible households on top.',
    pill: 'QLD',
    pillColor: 'bg-orange-100 text-orange-800',
  },
  {
    state: 'Western Australia',
    icon: '⬛',
    title: 'Federal STC',
    program: 'SRES',
    value: '$700 – $1,800*',
    detail:
      'Federal STC discount applies. Synergy customers can pair with off-peak smart-timing for further savings.',
    pill: 'WA',
    pillColor: 'bg-gray-100 text-gray-800',
  },
  {
    state: 'Tasmania',
    icon: '🟩',
    title: 'Federal STC + Energy Saver Loan',
    program: 'SRES + TAS Energy Saver Loan Scheme',
    value: '$700 – $1,800 + 0% loan*',
    detail:
      'Federal STC plus access to 0% interest loans up to $10,000 for energy-efficient upgrades.',
    pill: 'TAS',
    pillColor: 'bg-green-100 text-green-800',
  },
  {
    state: 'ACT',
    icon: '🟪',
    title: 'EEIS + Federal STC',
    program: 'Energy Efficiency Improvement Scheme',
    value: '$800 – $2,500*',
    detail:
      'Highest ACT rebates for priority households. Stackable with federal STCs.',
    pill: 'ACT',
    pillColor: 'bg-purple-100 text-purple-800',
  },
  {
    state: 'Northern Territory',
    icon: '🟫',
    title: 'Federal STC',
    program: 'SRES',
    value: '$700 – $1,800*',
    detail:
      'Federal STC discount available. Tropical-rated models recommended for NT conditions.',
    pill: 'NT',
    pillColor: 'bg-amber-100 text-amber-800',
  },
];

const comparison = [
  {
    type: 'Heat Pump',
    energy: '900 – 1,200 kWh/yr*',
    cost: '$280 – $400/yr*',
    co2: 'Very Low',
    badge: 'Best Choice',
    highlight: true,
  },
  {
    type: 'Solar Electric Boost',
    energy: '1,500 – 2,500 kWh/yr*',
    cost: '$450 – $750/yr*',
    co2: 'Low',
    badge: null,
  },
  {
    type: 'Gas Storage',
    energy: '~20,000 MJ/yr*',
    cost: '$700 – $950/yr*',
    co2: 'High',
    badge: null,
  },
  {
    type: 'Electric Storage',
    energy: '3,500 – 4,500 kWh/yr*',
    cost: '$1,100 – $1,500/yr*',
    co2: 'Very High',
    badge: 'Most Expensive',
    highlight: false,
    warning: true,
  },
];

const faqs = [
  {
    q: 'How much can I really save?',
    a: 'Switching from an electric storage system, most households save $800–$1,200 per year.* Switching from gas saves $400–$700 per year.* Pair with solar and savings can exceed $1,500/year.*',
  },
  {
    q: 'Are heat pumps reliable in cold weather?',
    a: 'Yes. CO₂-refrigerant heat pumps (like Reclaim and Sanden) perform efficiently down to -10°C ambient.* R290/R32 models work reliably across most of Australia, including many alpine zones — actual performance is model-dependent.',
  },
  {
    q: 'How long does installation take?',
    a: 'A standard like-for-like swap is typically completed in 3–5 hours by our CEC-accredited installers.* Complex jobs (switchboard upgrade, gas decommissioning, relocations) may take longer. Old system removal and disposal is included.',
  },
  {
    q: 'Do I need to be home during install?',
    a: 'Someone over 18 needs to be on-site briefly to sign paperwork and for the system handover walk-through.',
  },
  {
    q: 'Can I claim multiple rebates at once?',
    a: 'In most cases — Federal STCs can be stacked with many state programs (VEECs, ESCs, REPS, EEIS), subject to scheme rules and eligibility.* We calculate every available rebate at the time of quoting and apply them as point-of-sale discounts where eligible.',
  },
  {
    q: 'What size do I need?',
    a: '1–2 people: 170L. 3–4 people: 250–270L. 5+ people: 315–400L. Our team confirms sizing based on your hot water habits and bathroom count.',
  },
];

const HeatPumpPage = () => {
  const { openModal } = useEstimateModal();
  const [savings, setSavings] = useState(0);

  // Animated savings counter
  useEffect(() => {
    const target = 1200;
    const duration = 1500;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      setSavings(Math.floor(target * p));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className="min-h-screen" data-testid="heat-pump-page">
      <SEO
        title="Heat Pump Hot Water Systems Australia — Up to 75% Less Energy*"
        description="Heat pump hot water systems by Reclaim, Sanden, iStore, Aquatech, Stiebel Eltron, Neopower, Econova, Haier, Emerald and Midea — installed by CEC-accredited Supa Energy Hub team with federal STCs and state rebates applied upfront."
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          },
          {
            '@context': 'https://schema.org',
            '@type': 'Service',
            serviceType: 'Heat Pump Hot Water Installation',
            provider: { '@type': 'Organization', name: 'Supa Energy Hub' },
            areaServed: { '@type': 'Country', name: 'Australia' },
            description:
              'Supply and install of heat pump hot water systems with federal STCs and state rebates applied upfront.',
          },
        ]}
      />
      <Header />
      <main>
        {/* HERO */}
        <section className="pt-32 pb-20 bg-gradient-to-br from-[#f8faf5] via-white to-[#f0f7e6] relative overflow-hidden">
          <div className="absolute top-20 right-10 w-72 h-72 bg-[#B5D43B]/15 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-[#3D5A47]/5 rounded-full blur-3xl" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#B5D43B]/20 rounded-full mb-5">
                  <Sparkles className="w-4 h-4 text-[#3D5A47]" />
                  <span className="text-xs font-semibold tracking-wide uppercase text-[#3D5A47]">
                    Heat Pump Hot Water Systems
                  </span>
                </div>
                <h1 className="heading-1 mb-5">
                  Hot Water for Up to{' '}
                  <span className="text-[#B5D43B]">75% Less*</span> — Every Single Day.
                </h1>
                <p className="body-large text-gray-700 mb-8 max-w-xl">
                  Australia's smartest way to heat water. Slash bills, ditch gas, and stack federal + state rebates — installed by CEC-accredited Supa Energy Hub experts.
                </p>

                {/* Trust strip */}
                <div className="flex flex-wrap items-center gap-4 mb-8 text-sm text-[#3D5A47]">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#B5D43B]" />
                    <span className="font-medium">From $33/week*</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#B5D43B]" />
                    <span className="font-medium">CEC-Accredited Installers</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#B5D43B]" />
                    <span className="font-medium">Rebates Applied Upfront</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4">
                  <Button
                    data-testid="heat-pump-hero-cta"
                    onClick={openModal}
                    className="btn-primary gap-2 px-6 py-3 h-auto text-base"
                  >
                    Get Free Quote
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                  <a href="#rebates">
                    <Button className="btn-secondary gap-2 px-6 py-3 h-auto text-base">
                      See Rebates In My State
                      <MapPin className="w-4 h-4" />
                    </Button>
                  </a>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  *Subject to finance approval & eligibility. Indicative only — see disclaimers below.
                </p>
              </div>

              {/* Hero visual + savings card */}
              <div className="relative">
                <div className="rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="/heat-pump-images/istore-home-lifestyle.webp"
                    alt="iStore heat pump installed at a modern Australian home with rooftop solar"
                    loading="eager"
                    className="w-full aspect-[4/3] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#3D5A47]/30 to-transparent" />
                </div>

                {/* Floating savings badge */}
                <div className="absolute -bottom-6 -left-3 sm:-left-6 bg-white rounded-2xl shadow-xl p-5 border border-[#B5D43B]/30 max-w-[240px]">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="w-5 h-5 text-[#B5D43B]" />
                    <span className="text-xs font-semibold tracking-wide uppercase text-[#3D5A47]">
                      Avg. Annual Saving
                    </span>
                  </div>
                  <div className="text-3xl font-bold text-[#3D5A47]">
                    ${savings.toLocaleString()}*
                    <span className="text-sm font-normal text-gray-500">/yr</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    vs electric storage system
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* QUICK STATS STRIP */}
        <section className="py-10 bg-[#3D5A47] text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { v: '75%*', l: 'Less Energy Used' },
                { v: '$1,200*', l: 'Avg Yearly Saving' },
                { v: '5x*', l: 'More Efficient' },
                { v: '10–15 yr*', l: 'Typical Lifespan' },
              ].map((s, i) => (
                <div key={i}>
                  <div className="text-3xl md:text-4xl font-bold text-[#B5D43B]">
                    {s.v}
                  </div>
                  <div className="text-sm mt-1 text-white/80">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHY HEAT PUMP — Benefits */}
        <section className="py-20 bg-white" id="benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <span className="inline-block text-[#B5D43B] font-semibold text-sm tracking-wider uppercase mb-3">
                Why Switch
              </span>
              <h2 className="heading-2 mb-4">
                The smartest hot water decision you'll make this decade.
              </h2>
              <p className="body-large text-gray-600">
                Heat pumps are the same proven technology in your fridge — used in reverse — to deliver 3–5x more heat than the electricity they consume.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((b, i) => (
                <div
                  key={i}
                  className="bg-gray-50 p-7 rounded-2xl hover:shadow-lg hover:-translate-y-1 transition-all border border-transparent hover:border-[#B5D43B]/30"
                  data-testid={`benefit-card-${i}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#B5D43B]/20 flex items-center justify-center mb-4">
                    <b.icon className="w-6 h-6 text-[#3D5A47]" />
                  </div>
                  <h3 className="font-semibold text-lg text-[#3D5A47] mb-2">
                    {b.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {b.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-20 bg-gradient-to-br from-[#f8faf5] to-[#f0f7e6]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block text-[#B5D43B] font-semibold text-sm tracking-wider uppercase mb-3">
                  How It Works
                </span>
                <h2 className="heading-2 mb-6">
                  Free heat from thin air — literally.
                </h2>
                <p className="text-gray-600 mb-8">
                  A heat pump doesn't "make" heat — it moves it. By extracting warmth from the surrounding air, it heats your water using a fraction of the electricity of conventional systems.*
                </p>
                <div className="space-y-5">
                  {howItWorks.map((s, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#3D5A47] text-[#B5D43B] flex items-center justify-center font-bold">
                        {s.step}
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#3D5A47] mb-1">
                          {s.title}
                        </h4>
                        <p className="text-sm text-gray-600">{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <div className="rounded-3xl overflow-hidden shadow-xl bg-gradient-to-br from-[#f0f7e6] to-white">
                  <img
                    src="/heat-pump-images/istore-real-install.jpg"
                    alt="Real iStore heat pump installation at an Australian residential home"
                    loading="lazy"
                    className="w-full aspect-[4/5] object-cover"
                  />
                </div>
                <div className="absolute -top-5 -right-3 sm:-right-5 bg-white rounded-2xl shadow-xl p-5 border border-[#B5D43B]/30">
                  <div className="flex items-center gap-2 mb-1">
                    <ThermometerSun className="w-5 h-5 text-[#B5D43B]" />
                    <span className="text-xs font-semibold uppercase text-[#3D5A47]">
                      Efficiency (COP)
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-[#3D5A47]">
                    3.5 – 5.0*
                  </div>
                  <p className="text-xs text-gray-600">
                    vs 1.0 for electric element
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* COMPARISON TABLE */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="inline-block text-[#B5D43B] font-semibold text-sm tracking-wider uppercase mb-3">
                Side-by-Side
              </span>
              <h2 className="heading-2 mb-4">
                Heat pump vs gas vs electric: the running costs.
              </h2>
              <p className="text-gray-600">
                Typical 4-person household.* Based on national averages — savings vary by usage and tariff.
              </p>
            </div>

            <div className="overflow-x-auto rounded-2xl shadow-lg border border-gray-100">
              <table className="w-full text-left">
                <thead className="bg-[#3D5A47] text-white">
                  <tr>
                    <th className="px-6 py-4 text-sm font-semibold">System Type</th>
                    <th className="px-6 py-4 text-sm font-semibold">Yearly Energy Use</th>
                    <th className="px-6 py-4 text-sm font-semibold">Yearly Running Cost</th>
                    <th className="px-6 py-4 text-sm font-semibold">CO₂ Emissions</th>
                    <th className="px-6 py-4 text-sm font-semibold"></th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((c, i) => (
                    <tr
                      key={i}
                      className={`border-b border-gray-100 ${
                        c.highlight ? 'bg-[#B5D43B]/10' : 'bg-white'
                      }`}
                    >
                      <td className="px-6 py-4 font-semibold text-[#3D5A47] flex items-center gap-2">
                        {c.highlight && (
                          <CheckCircle className="w-5 h-5 text-[#B5D43B]" />
                        )}
                        {c.type}
                      </td>
                      <td className="px-6 py-4 text-gray-700">{c.energy}</td>
                      <td className="px-6 py-4 text-gray-700 font-medium">
                        {c.cost}
                      </td>
                      <td className="px-6 py-4 text-gray-700">{c.co2}</td>
                      <td className="px-6 py-4">
                        {c.badge && (
                          <span
                            className={`text-xs font-semibold px-3 py-1 rounded-full ${
                              c.highlight
                                ? 'bg-[#B5D43B] text-[#3D5A47]'
                                : c.warning
                                ? 'bg-red-100 text-red-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {c.badge}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* REBATES */}
        <section
          id="rebates"
          className="py-20 bg-gradient-to-br from-[#3D5A47] to-[#2d4435] text-white relative overflow-hidden"
        >
          <div className="absolute top-10 right-10 w-72 h-72 bg-[#B5D43B]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-[#B5D43B]/5 rounded-full blur-3xl" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center max-w-3xl mx-auto mb-14">
              <span className="inline-block text-[#B5D43B] font-semibold text-sm tracking-wider uppercase mb-3">
                Government Rebates & Incentives
              </span>
              <h2 className="text-4xl sm:text-5xl font-bold mb-4">
                Stack <span className="text-[#B5D43B]">Federal + State</span> rebates and save thousands.
              </h2>
              <p className="text-white/80 text-lg">
                Every Australian heat pump install is eligible for federal STCs. Most states offer additional point-of-sale rebates. We handle the paperwork — you just enjoy the discount.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {rebates.map((r, i) => (
                <div
                  key={i}
                  className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/15 transition-all hover:-translate-y-1"
                  data-testid={`rebate-card-${i}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-2xl">{r.icon}</span>
                    <span
                      className={`text-xs font-bold px-3 py-1 rounded-full ${r.pillColor}`}
                    >
                      {r.pill}
                    </span>
                  </div>
                  <div className="text-xs text-[#B5D43B] font-semibold uppercase tracking-wider mb-2">
                    {r.state}
                  </div>
                  <h3 className="text-lg font-bold mb-1">{r.title}</h3>
                  <p className="text-xs text-white/60 mb-4">{r.program}</p>
                  <div className="text-3xl font-bold text-[#B5D43B] mb-3">
                    {r.value}
                  </div>
                  <p className="text-sm text-white/80 leading-relaxed">
                    {r.detail}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <div className="inline-flex flex-col sm:flex-row items-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-6 py-4">
                <FileCheck className="w-6 h-6 text-[#B5D43B]" />
                <p className="text-sm">
                  We do all the paperwork. Rebates are applied as upfront discounts — no waiting, no claims.
                </p>
                <Button
                  data-testid="heat-pump-rebate-cta"
                  onClick={openModal}
                  className="bg-[#B5D43B] text-[#3D5A47] hover:bg-[#a4c032] font-semibold"
                >
                  Check My Eligibility
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* BRANDS */}
        <section className="py-20 bg-white" id="brands">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <span className="inline-block text-[#B5D43B] font-semibold text-sm tracking-wider uppercase mb-3">
                Trusted Brands
              </span>
              <h2 className="heading-2 mb-4">
                We install only the brands we'd put in our own homes.
              </h2>
              <p className="text-gray-600">
                10 of Australia's most-trusted heat pump manufacturers — from premium CO₂ refrigerant pioneers to high-value efficiency leaders.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
              {brands.map((b, i) => {
                const cardInner = (
                  <>
                    {/* Logo */}
                    <div
                      className={`h-20 rounded-xl mb-4 flex items-center justify-center px-4 ${
                        b.logoBg || 'bg-gray-50'
                      } group-hover:bg-gradient-to-br group-hover:from-gray-50 group-hover:to-white transition-colors`}
                    >
                      <img
                        src={b.logo}
                        alt={`${b.name} logo`}
                        loading="lazy"
                        className="max-h-12 max-w-full w-auto h-auto object-contain"
                      />
                    </div>

                    {/* Name */}
                    <h3 className="font-bold text-[#3D5A47] text-sm text-center mb-1">
                      {b.name}
                      {b.slug && (
                        <span className="ml-1 text-[10px] text-[#B5D43B] font-semibold uppercase tracking-wider">
                          · View
                        </span>
                      )}
                    </h3>
                    <p className="text-[11px] text-gray-500 text-center leading-snug mb-4 min-h-[2rem]">
                      {b.tagline}
                    </p>

                    {/* Feature pills */}
                    <div className="flex flex-wrap gap-1.5 justify-center mt-auto">
                      {b.pills.map((p, j) => (
                        <span
                          key={j}
                          className="inline-flex items-center gap-1 text-[10px] font-medium text-[#3D5A47] bg-[#B5D43B]/15 border border-[#B5D43B]/30 rounded-full px-2 py-1 leading-none"
                        >
                          <p.icon className="w-3 h-3" strokeWidth={2.5} />
                          {p.label}
                        </span>
                      ))}
                    </div>
                  </>
                );

                const className = `group bg-white rounded-2xl p-5 border border-gray-200 hover:border-[#B5D43B]/60 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col ${
                  b.slug ? 'cursor-pointer' : ''
                }`;

                return b.slug ? (
                  <Link
                    key={i}
                    to={`/brands/${b.slug}`}
                    className={className}
                    data-testid={`brand-card-${i}`}
                  >
                    {cardInner}
                  </Link>
                ) : (
                  <div key={i} className={className} data-testid={`brand-card-${i}`}>
                    {cardInner}
                  </div>
                );
              })}
            </div>

            <div className="text-center mt-10">
              <p className="text-sm text-gray-500 mb-4">
                Not sure which brand suits your home? Our team will recommend the perfect match for your usage, climate zone & budget.
              </p>
              <Button
                data-testid="heat-pump-brand-cta"
                onClick={openModal}
                className="btn-primary gap-2"
              >
                Get Tailored Recommendation
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </section>

        {/* WHO IT'S FOR / SOLAR PAIRING */}
        <section className="py-20 bg-gradient-to-br from-[#f8faf5] to-[#f0f7e6]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <div className="rounded-3xl overflow-hidden shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1758523419540-035814b85c80?crop=entropy&cs=srgb&fm=jpg&q=85"
                    alt="Australian family celebrating lower energy bills"
                    className="w-full aspect-[4/3] object-cover"
                  />
                </div>
                <div className="absolute -bottom-5 -right-3 sm:-right-5 bg-[#B5D43B] rounded-2xl shadow-xl p-5 max-w-[220px]">
                  <div className="flex items-center gap-2 mb-1">
                    <Sun className="w-5 h-5 text-[#3D5A47]" />
                    <span className="text-xs font-bold uppercase text-[#3D5A47]">
                      Solar Pairing
                    </span>
                  </div>
                  <p className="text-sm font-medium text-[#3D5A47] leading-snug">
                    Run on free daytime solar — water becomes effectively free.*
                  </p>
                </div>
              </div>

              <div>
                <span className="inline-block text-[#B5D43B] font-semibold text-sm tracking-wider uppercase mb-3">
                  Perfect For
                </span>
                <h2 className="heading-2 mb-6">
                  Built for everyday Australian homes.
                </h2>
                <div className="space-y-4">
                  {[
                    {
                      icon: HomeIcon,
                      title: 'Replacing old electric storage',
                      desc: "Save $800–$1,500+ a year* and reclaim your home's biggest electricity drain.",
                    },
                    {
                      icon: Flame,
                      title: 'Getting off gas',
                      desc: 'Eliminate gas supply charges and reduce bills + emissions in one move.',
                    },
                    {
                      icon: Sun,
                      title: 'Homes with solar',
                      desc: 'Use excess daytime solar to heat water — turn export into instant savings.',
                    },
                    {
                      icon: HeartHandshake,
                      title: 'Pensioners & concession holders',
                      desc: 'Eligible for additional state rebates and concessional pricing in most states.',
                    },
                  ].map((p, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-[#B5D43B]/20 flex items-center justify-center">
                        <p.icon className="w-5 h-5 text-[#3D5A47]" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#3D5A47]">
                          {p.title}
                        </h4>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {p.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PROCESS / INSTALL */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <span className="inline-block text-[#B5D43B] font-semibold text-sm tracking-wider uppercase mb-3">
                Our Process
              </span>
              <h2 className="heading-2 mb-4">
                From quote to hot water in 5 simple steps.
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {[
                { icon: Phone, t: 'Free Quote', d: '30-second form, no obligation.' },
                { icon: HomeIcon, t: 'Site Assessment', d: 'We confirm sizing & rebates.' },
                { icon: FileCheck, t: 'Paperwork Sorted', d: 'We claim every rebate for you.' },
                { icon: Wrench, t: 'Installation', d: '3–5 hours by CEC-accredited team.*' },
                { icon: Droplet, t: 'Hot Water 24/7', d: 'Enjoy free heat from the air.' },
              ].map((s, i) => (
                <div
                  key={i}
                  className="text-center relative"
                  data-testid={`process-step-${i}`}
                >
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-[#3D5A47] text-[#B5D43B] flex items-center justify-center mb-4 relative z-10">
                    <s.icon className="w-7 h-7" />
                  </div>
                  <div className="text-xs font-bold text-[#B5D43B] uppercase mb-1">
                    Step {i + 1}
                  </div>
                  <h4 className="font-semibold text-[#3D5A47] mb-1">{s.t}</h4>
                  <p className="text-sm text-gray-600">{s.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="inline-block text-[#B5D43B] font-semibold text-sm tracking-wider uppercase mb-3">
                FAQs
              </span>
              <h2 className="heading-2 mb-4">Got questions? We've got answers.</h2>
            </div>
            <div className="space-y-4">
              {faqs.map((f, i) => (
                <details
                  key={i}
                  className="group bg-white rounded-2xl border border-gray-100 hover:border-[#B5D43B]/30 transition-colors"
                  data-testid={`faq-item-${i}`}
                >
                  <summary className="cursor-pointer list-none p-6 flex items-center justify-between gap-4">
                    <span className="font-semibold text-[#3D5A47]">{f.q}</span>
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#B5D43B]/20 flex items-center justify-center group-open:rotate-45 transition-transform">
                      <Zap className="w-4 h-4 text-[#3D5A47]" />
                    </span>
                  </summary>
                  <div className="px-6 pb-6 text-gray-600 text-sm leading-relaxed">
                    {f.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* DISCLAIMERS — Compliance & Legal */}
        <section
          id="disclaimers"
          className="py-14 bg-white border-t border-gray-100"
          data-testid="disclaimers-section"
        >
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <Info className="w-5 h-5 text-[#3D5A47]" strokeWidth={2.4} />
                <h2 className="text-lg sm:text-xl font-bold text-[#3D5A47]">
                  Important Information & Disclaimers
                  <span className="text-[#B5D43B] ml-1">*</span>
                </h2>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed mb-4">
                All figures, claims and references marked with an asterisk
                <span className="text-[#3D5A47] font-semibold"> (*) </span>
                throughout this page are <strong>indicative only</strong> and
                provided for general information. They do not constitute a
                quote, financial advice, energy advice, or a guarantee of
                outcome. Actual savings, costs, performance, rebate values,
                installation timeframes and product availability depend on
                your individual circumstances and may differ from the figures
                shown. Please obtain a written, site-specific quote from Supa
                Energy Hub before making any purchasing decisions.
              </p>

              <ol className="list-decimal pl-5 space-y-2 text-xs text-gray-600 leading-relaxed">
                <li>
                  <strong>Savings, bill, running-cost &amp; efficiency
                  claims</strong> (including "up to 75% less", "$1,200/yr
                  average saving", "5× more efficient", "$800–$1,500/yr",
                  "$1,500/year with solar", "$280–$400/yr running cost",
                  "3–5× more heat", COP 3.5–5.0 and similar) are typical-
                  household estimates derived from published industry data
                  (Sustainability Victoria, Climateworks, Energy Made Easy,
                  AEMC, IEA) and manufacturer datasheets. They are based on a
                  typical 4-person Australian household using ~165 L of hot
                  water per day on average grid tariffs. Your actual usage,
                  tariff, climate zone, hot-water demand and chosen system
                  will produce different results.
                </li>
                <li>
                  <strong>Coefficient of Performance (COP) figures</strong>
                  are manufacturer ratings measured under standard laboratory
                  test conditions (AS/NZS 4234 / AS/NZS 5125 / EN 16147).
                  Real-world COP varies with ambient air temperature,
                  refrigerant type, water-draw profile and tank stand-by
                  losses. Performance ranges quoted (e.g. "operates from
                  -10 °C to 43 °C ambient") are model-dependent — not every
                  heat pump operates across that full envelope. Our team
                  will recommend a model suited to your specific climate
                  zone.
                </li>
                <li>
                  <strong>Rebate values</strong> (STCs, VEECs, ESCs/PDRS,
                  REPS, EEIS, Solar Victoria, TAS Energy Saver Loan, QLD/NT
                  concessions and any combined ranges shown) are{' '}
                  <em>indicative ranges only</em> at the time of publishing.
                  Actual rebate amounts depend on installation zone, system
                  capacity, model eligibility, household income/eligibility
                  criteria, the prevailing market price of certificates, and
                  scheme rules set by the Clean Energy Regulator and the
                  relevant state agencies — all of which change from time to
                  time. Eligibility and the ability to stack rebates is
                  determined on a case-by-case basis. We will confirm the
                  exact rebate value at the point of formal quote.
                </li>
                <li>
                  <strong>Comparison table</strong> (heat pump vs solar boost
                  vs gas storage vs electric storage) reflects typical
                  4-person household assumptions and national-average tariffs
                  and is provided for illustrative comparison purposes only.
                  Not every system or household will achieve these figures.
                  Gas figures assume current network supply-charge averages.
                </li>
                <li>
                  <strong>Carbon &amp; environmental claims</strong>
                  ("up to 70% less CO₂", "effectively zero-emissions when
                  paired with solar", etc.) are based on grid-emissions
                  factors published by the Australian Department of Climate
                  Change, Energy, the Environment and Water (DCCEEW). Grid
                  intensity, your generation source and seasonal solar yield
                  will affect outcomes.
                </li>
                <li>
                  <strong>Brand descriptors</strong> such as "Global Top-5
                  Appliances" (Haier), "Major Global HVAC" (Midea), "Japanese
                  CO₂ heat pump pioneer" (Sanden), "German engineered"
                  (Stiebel Eltron) and other market-position statements
                  reflect publicly reported information from independent
                  industry analysts (e.g. Euromonitor International,
                  BSRIA, Daxue/JATO) and the manufacturers' own published
                  materials. Market positions are updated periodically and
                  may change. All trademarks, product names and logos are
                  the property of their respective owners and are used here
                  for identification and informational purposes only.
                </li>
                <li>
                  <strong>Installation timeframes</strong> ("3–5 hours by
                  CEC-accredited team") describe a typical, like-for-like
                  swap of an existing hot water system. Jobs requiring a
                  switchboard upgrade, gas decommissioning by a licensed
                  gasfitter, system relocation, structural works, asbestos
                  handling, or strata approval will take additional time and
                  may require additional trades and cost.
                </li>
                <li>
                  <strong>Product lifespan &amp; warranty</strong> ("10–15
                  year lifespan", "5–10 year tank warranties") are typical
                  manufacturer-published figures. Actual lifespan depends on
                  water quality, hardness, maintenance regime, model and
                  warranty terms. Warranty terms and conditions are set by
                  the manufacturer and supersede any general statement on
                  this page.
                </li>
                <li>
                  <strong>Noise levels</strong> ("37–48 dB") are
                  manufacturer-published sound pressure values measured at a
                  defined distance under controlled conditions. Real-world
                  perceived noise depends on placement, surrounding
                  surfaces, ambient noise and operating mode.
                </li>
                <li>
                  <strong>Finance offers</strong> ("from $33/week") are
                  illustrative only, are not an offer of credit, and are
                  subject to credit assessment, approval, terms, conditions
                  and fees of the relevant third-party finance provider.
                </li>
                <li>
                  <strong>Solar-pairing benefits</strong> ("free daytime
                  solar — water becomes effectively free") depend on the
                  size and performance of your solar PV system, your daytime
                  consumption profile, weather, retailer feed-in tariff and
                  the system's smart-timing capability.
                </li>
                <li>
                  <strong>Images</strong> on this page include both real
                  installation photographs and manufacturer/lifestyle
                  imagery. Where lifestyle imagery is shown, it is intended
                  to illustrate the product and typical installation context
                  — your home and finished installation will differ.
                </li>
                <li>
                  <strong>No professional advice.</strong> Information on
                  this page is general in nature and does not take into
                  account your individual objectives, financial situation,
                  energy usage or property characteristics. You should
                  obtain independent advice as appropriate before relying on
                  any information on this page.
                </li>
              </ol>

              <p className="text-xs text-gray-500 leading-relaxed mt-5 border-t border-gray-200 pt-4">
                Supa Energy Hub complies with the Australian
                Consumer Law (Schedule 2 of the Competition and Consumer Act
                2010 (Cth)). Nothing on this page is intended to mislead or
                deceive, exclude or limit any consumer guarantee that
                cannot lawfully be excluded. For a binding, site-specific
                quote and full rebate calculation, please request a free
                in-home or virtual assessment.
              </p>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-20 bg-[#B5D43B] relative overflow-hidden">
          <div className="absolute top-0 left-0 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#3D5A47]/10 rounded-full blur-3xl" />

          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <Award className="w-12 h-12 text-[#3D5A47] mx-auto mb-4" />
            <h2 className="text-4xl sm:text-5xl font-bold text-[#3D5A47] mb-4">
              Ready to stop overpaying for hot water?
            </h2>
            <p className="text-[#3D5A47]/80 text-lg mb-8 max-w-2xl mx-auto">
              Get your personalised heat pump quote — rebates calculated, install included. Takes 30 seconds.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                data-testid="heat-pump-final-cta"
                onClick={openModal}
                className="bg-[#3D5A47] hover:bg-[#2d4435] text-white gap-2 px-8 py-4 h-auto text-base font-semibold"
              >
                Get My Free Quote
                <ArrowRight className="w-4 h-4" />
              </Button>
              <Link to="/contact">
                <Button
                  data-testid="heat-pump-final-contact"
                  variant="outline"
                  className="border-[#3D5A47] text-[#3D5A47] hover:bg-[#3D5A47] hover:text-white gap-2 px-8 py-4 h-auto text-base font-semibold"
                >
                  Talk to a Specialist
                  <Phone className="w-4 h-4" />
                </Button>
              </Link>
            </div>
            <p className="text-xs text-[#3D5A47]/70 mt-5">
              No obligation · No pressure · 100% Australian team
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default HeatPumpPage;
